import React from "react";
import Card from "antd/lib/card";
import Avatar from "antd/lib/avatar/avatar";
import {
	getAvatarBackgroundColor,
	getAvatarInitials
} from "@/Contracts/Utils";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import moment from "moment";
import { IEmailThreadMessageHeaderProps } from "./interface";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ZTooltip from "@atoms/ZTooltip";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import ZDropdown from "@molecules/ZDropdown";
import ZMenu from "@molecules/ZMenu";

const { ZText } = ZTypography;

const ZEmailThreadMessageHeader: React.FC<IEmailThreadMessageHeaderProps> = ({
	from,
	to,
	date,
	cc,
	bcc,
	replyTo,
	subject,
	snippet,
	files,
	onReply,
	isOthersEmailThread
}) => {
	const stopPropagation = (
		$event: React.MouseEvent<HTMLElement, MouseEvent>
	) => {
		$event.stopPropagation();
	};
	const avatarInitials = getAvatarInitials(from.name ?? "");
	const dateString = moment(date).format("h:mm a, MMMM Do YYYY");

	const checkThreadAttachment = () => {
		const isAttachmentAvailable = files?.filter(
			file => file.content_disposition !== "inline"
		);
		return !!isAttachmentAvailable?.length;
	};

	return (
		<Card bordered={false} className="email-thread-message-card">
			<Card.Meta
				avatar={
					<Avatar
						className="email-thread-message-avatar"
						alt="Email Thread"
						style={{
							backgroundColor:
								getAvatarBackgroundColor(avatarInitials)
						}}
					>
						{getAvatarInitials(avatarInitials)}
					</Avatar>
				}
				title={
					<Row className="email-thread-message-header" align="middle">
						<div
							className="email-details"
							onClick={stopPropagation}
						>
							<Row>
								<Col
									span={16}
									className="email-thread-message-sender-container"
								>
									{from.name && (
										<ZText
											weight={EFontWeights.MEDIUM}
											className="email-thread-message-from-name"
										>
											{from.name}
										</ZText>
									)}
									<ZText className="email-thread-message-from-email">
										{from.name && " "}
										{`<${from.email}>`}
									</ZText>
								</Col>
								<Col
									span={8}
									className="email-thread-message-timestamp-reply-icon-container"
								>
									{files &&
										files.length > 0 &&
										checkThreadAttachment() && (
											<ZIcon
												name={EIcons.ATTACHMENT}
												className="email-thread-message-reply-icon upload-icon h-4 mx-0"
												fill={false}
												colorVariant={8}
											/>
										)}
									<ZText
										level={0}
										colorVariant={8}
										className="email-thread-message-timestamp"
									>
										{dateString}
									</ZText>
									{onReply && (
										<ZTooltip
											placement={
												isOthersEmailThread
													? "left"
													: "top"
											}
											title={
												isOthersEmailThread
													? `Reply is disabled as this email belongs to another user's inbox.`
													: "Reply"
											}
										>
											<div
												className={`email-thread-message-reply-icon-container ${
													isOthersEmailThread
														? "disabled cursor-auto"
														: ""
												}`}
												onClick={e => {
													stopPropagation(e);
													!isOthersEmailThread &&
														onReply();
												}}
											>
												<ZIcon
													viewBox="0 0 12 12"
													name={EIcons.EMAILREPLY}
													className="email-thread-message-reply-icon"
													fill
												/>
											</div>
										</ZTooltip>
									)}
								</Col>
							</Row>
						</div>

						<Row className="email-thread-message-receiver-container">
							<Col span={24} className="flex items-center">
								<ZText className="email-thread-message-to-email">
									To:{" "}
									{to
										.map(({ name, email }) =>
											name ? name.split(" ")[0] : email
										)
										.join(", ")}
								</ZText>
								<ZDropdown
									overlay={
										<ZMenu className="email-thread-message-receiver-dropdown-menu">
											<ZMenu.Item key="0">
												<Row onClick={stopPropagation}>
													<Col span={24}>
														<Row
															gutter={8}
															className="email-thread-message-receiver-section"
														>
															<Col
																span={6}
																className="info-container-label"
															>
																<ZText>
																	From:
																</ZText>
															</Col>
															<Col
																span={18}
																className="info-container-value"
															>
																<ZText>
																	{from.name
																		? `${from.name} <${from.email}>`
																		: from.email}
																</ZText>
															</Col>
														</Row>
														<Row
															gutter={8}
															className="email-thread-message-receiver-section"
														>
															<Col
																span={6}
																className="info-container-label"
															>
																<ZText>
																	To:
																</ZText>
															</Col>
															<Col
																span={18}
																className="info-container-value"
															>
																{to.map(
																	(
																		{
																			name,
																			email
																		},
																		toIndex
																	) => {
																		return (
																			<ZText
																				key={`${toIndex}-${email}`}
																			>
																				{name
																					? `${name} <${email}>`
																					: email}
																				{toIndex !==
																					to.length -
																						1 &&
																					", "}
																			</ZText>
																		);
																	}
																)}
															</Col>
														</Row>
														{cc && !!cc.length && (
															<Row
																gutter={8}
																className="email-thread-message-receiver-section"
															>
																<Col
																	span={6}
																	className="info-container-label"
																>
																	<ZText>
																		CC:
																	</ZText>
																</Col>
																<Col
																	span={18}
																	className="info-container-value"
																>
																	{cc.map(
																		(
																			{
																				name,
																				email
																			},
																			ccIndex
																		) => {
																			return (
																				<ZText
																					key={`${ccIndex}-${email}`}
																				>
																					{name
																						? `${name} <${email}>`
																						: email}
																					{ccIndex !==
																						cc.length -
																							1 &&
																						", "}
																				</ZText>
																			);
																		}
																	)}
																</Col>
															</Row>
														)}
														{bcc &&
															!!bcc.length && (
																<Row
																	gutter={8}
																	className="email-thread-message-receiver-section"
																>
																	<Col
																		span={6}
																		className="info-container-label"
																	>
																		BCC:
																	</Col>
																	<Col
																		span={
																			18
																		}
																		className="info-container-value"
																	>
																		{bcc.map(
																			(
																				{
																					name,
																					email
																				},
																				bccIndex
																			) => {
																				return (
																					<ZText
																						key={`${bccIndex}-${email}`}
																					>
																						{name
																							? `${name} <${email}>`
																							: email}
																						{bccIndex !==
																							bcc.length -
																								1 &&
																							", "}
																					</ZText>
																				);
																			}
																		)}
																	</Col>
																</Row>
															)}
														{replyTo &&
															!!replyTo.length && (
																<Row
																	gutter={8}
																	className="email-thread-message-receiver-section"
																>
																	<Col
																		span={6}
																		className="info-container-label"
																	>
																		<ZText>
																			Reply
																			To:
																		</ZText>
																	</Col>
																	<Col
																		span={
																			18
																		}
																		className="info-container-value"
																	>
																		{replyTo.map(
																			(
																				{
																					name,
																					email
																				},
																				replyToIndex
																			) => {
																				return (
																					<ZText
																						key={`${replyToIndex}-${email}`}
																					>
																						{name
																							? `${name} <${email}>`
																							: email}
																						{replyToIndex !==
																							to.length -
																								1 &&
																							", "}
																					</ZText>
																				);
																			}
																		)}
																	</Col>
																</Row>
															)}
														<Row
															gutter={8}
															className="email-thread-message-receiver-section"
														>
															<Col
																span={6}
																className="info-container-label"
															>
																<ZText>
																	Date:
																</ZText>
															</Col>
															<Col
																span={18}
																className="info-container-value"
															>
																<ZText>
																	{dateString}
																</ZText>
															</Col>
														</Row>
														<Row
															gutter={8}
															className="email-thread-message-receiver-section"
														>
															<Col
																span={6}
																className="info-container-label"
															>
																<ZText>
																	Subject:
																</ZText>
															</Col>
															<Col
																span={18}
																className="info-container-value"
															>
																<ZText>
																	{subject &&
																	!!subject.length
																		? subject
																		: "[NO SUBJECT]"}
																</ZText>
															</Col>
														</Row>
													</Col>
												</Row>
											</ZMenu.Item>
										</ZMenu>
									}
								>
									<span
										onClick={stopPropagation}
										className="email-thread-message-to-caret"
									>
										<ZIcon
											name={Icons.CHEVRONDOWN}
											size="sm"
										/>
									</span>
								</ZDropdown>
							</Col>
						</Row>
						<Row>
							<Col
								span={24}
								className="email-thread-message-snippet-container"
							>
								<div className="email-thread-message-snippet">
									{snippet}
								</div>
							</Col>
						</Row>
					</Row>
				}
			/>
		</Card>
	);
};

export default ZEmailThreadMessageHeader;
